import { useState, useRef, useEffect, type JSX, type RefObject } from 'react';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import type { GridApi } from 'ag-grid-community';

interface ExportButtonProps {
  gridApiRef: RefObject<GridApi | null>;
  fileName?: string;
}

/**
 * Parse a CSV string (as emitted by AG Grid's getDataAsCsv) into rows of
 * cells. Handles quoted fields with embedded commas and escaped double-
 * quotes ("") per RFC 4180. Returned shape is row-major; first row is the
 * header. Empty rows (all-blank cells) are filtered out.
 *
 * Shared between handleXLSX and handlePDF — extracted from the inline
 * parser that previously lived only in handleXLSX (Day 57).
 */
function parseCsv(csv: string): string[][] {
  return csv
    .split('\n')
    .map((row: string): string[] => {
      const fields: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '"') {
          if (inQuotes && row[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          fields.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      fields.push(current);
      return fields;
    })
    .filter((row) => row.some((cell) => cell !== ''));
}

/**
 * ExportButton — dropdown with CSV, XLSX, and PDF options.
 * CSV uses AG Grid's exportDataAsCsv. XLSX parses CSV → SheetJS workbook.
 * PDF lazy-loads @react-pdf/renderer + ExportTablePdfDocument at click time
 * (per ADR Decision 32) to keep the lib out of every initial route chunk.
 */
export default function ExportButton({
  gridApiRef,
  fileName = 'arbi-export',
}: ExportButtonProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [pdfBusy, setPdfBusy] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleCSV = (): void => {
    gridApiRef.current?.exportDataAsCsv({ fileName: `${fileName}.csv` });
    setOpen(false);
  };

  const handleXLSX = (): void => {
    const api = gridApiRef.current;
    if (!api) return;
    const csvData = api.getDataAsCsv();
    if (!csvData) return;
    const rows = parseCsv(csvData);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${fileName}.xlsx`);
    setOpen(false);
  };

  const handlePDF = async (): Promise<void> => {
    const api = gridApiRef.current;
    if (!api) return;
    const csvData = api.getDataAsCsv();
    if (!csvData) return;
    const rows = parseCsv(csvData);
    setPdfBusy(true);
    try {
      const [{ pdf }, { default: ExportTablePdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../pdf/ExportTablePdfDocument'),
      ]);
      const blob = await pdf(<ExportTablePdfDocument rows={rows} title={fileName} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[ExportButton] PDF generation failed:', e);
    } finally {
      setPdfBusy(false);
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="arbi-btn-ghost text-xs px-2 py-1"
        title="Export"
        aria-label="Export data"
        aria-expanded={open}
      >
        <Download size={12} /> Export
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-surface-2 border border-border rounded shadow-md z-[100] min-w-[100px] overflow-hidden">
          <button
            onClick={handleCSV}
            className="w-full text-left px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
          >
            CSV
          </button>
          <button
            onClick={handleXLSX}
            className="w-full text-left px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
          >
            XLSX
          </button>
          <button
            onClick={handlePDF}
            disabled={pdfBusy}
            className="w-full text-left px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-pdf-option"
          >
            {pdfBusy ? 'Generating PDF…' : 'PDF'}
          </button>
        </div>
      )}
    </div>
  );
}
